import PropTypes from 'prop-types';
import {
  useCallback, useEffect, useRef,
} from 'react';
import {createUseStyles, useTheme} from 'react-jss';
import {connect} from 'react-redux';
import useDidUpdateEffect from '../../../../../hooks/useDidUpdateEffect';
import {useDeliveryAddressForBilling} from '../../../../../v2/actions/address-actions';
import {updateNewCardDetails} from '../../../../../v2/actions/paymentActions/payment-actions';
import AddressAsText from '../../../../../v2/components/address/AddressAsText/AddressAsText';
import BillingAddress from '../../../../../v2/components/address/BillingAddress';
import FieldContainer2 from '../../../../../v2/form/FieldContainer2';
import Translation from '../../../../../v2/lang/Translation';
import {getCardType} from '../../../../../v2/selectors/CardOptionSelectors/CardOptionSelectors';
import {isUsingDifferentBillingAddress} from '../../../../../v2/selectors/DeliveryAddressSelectors/DeliveryAddressSelectors';
import getIsExpiryDateWave2Enabled from '../../../../../v2/selectors/getIsExpiryDateWave2Enabled';
import getHasOptionalPaymentDetailsConsentCheckbox from '../../../../../v2/selectors/PaymentOptionSelectors/getHasOptionalPaymentDetailsConsentCheckbox';
import {getHasStoredCard} from '../../../../../v2/selectors/PaymentOptionSelectors/getHasStoredCard';
import {
  getSelectedPaymentOptionType,
  getSettings, getSiteCode,
  shouldCaptureBillingAddress,
} from '../../../../../v2/selectors/selectors';
import Spacing from '../../../../../v2/style-helper/Spacing';
import V2Components from '../../../../../v2/v2-components';
import StorePaymentOptionConsent from '../../../StorePaymentOptionConsent/StorePaymentOptionConsent';
import ExpandingBillingAddressLayout from './ExpandingBillingAddressLayout';
import getBillingAddressTickboxText from './getBillingAddressTickboxText';
import {CreditCardFields, UseShippingAddressField} from './NewCardFormFields';
import NewCardFormFieldSet from './NewCardFormFieldSet';

const useStyles = createUseStyles({
  newCardForm: {
    marginTop: Spacing.double,
    marginRight: props => (!props.hasStoredCard ? Spacing.single : 40),
  },
  
  addressAsText: {
    marginTop: Spacing.double,
  },
  
  billingAddressTitle: {
    marginTop: Spacing.triple,
    fontWeight: props => props.theme.inputLabelFontWeight,
  },
  
  billingAddress: {
    margin: [Spacing.triple, 0, Spacing.single],
  },
  
  billingAddressCheckboxContainer: {
    margin: [Spacing.quad, 0, Spacing.double],
  },
});

export function NewCardForm(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  const fieldContainerRef = useRef(null);
  
  const processErrorFieldName = errors => errors.map(error => ({
    fieldName: `paymentCard.${error.fieldName}`,
    errorCode: error.errorCode,
  }));
  
  const validate = useCallback(
    () => {
      if (!fieldContainerRef.current) return {isValid: true};
      const containerValidationResult = fieldContainerRef.current.validate();
      return {
        isValid: containerValidationResult.isValid,
        errors: (containerValidationResult.isValid) ? undefined
          : processErrorFieldName(containerValidationResult.errors),
      };
    }, [fieldContainerRef],
  );
  
  useEffect(() => {
    if (props.isSelected) {
      V2Components.register('newCard', {validate});
    } else {
      V2Components.clear('newCard');
    }
    
    return () => V2Components.clear('newCard');
  }, [props.isSelected, validate]);
  
  useDidUpdateEffect(() => {
    if (props.newCardDetails.cv2?.length > 2) {
      fieldContainerRef.current.validate('cv2');
    }
  }, [props.cardType.cv2Length, props.newCardDetails.cv2?.length]);
  
  const updateNewCardDetailsHandler = (fieldName, fieldValue) => {
    if (fieldName === 'useShippingAddress') {
      props.useDeliveryAddressForBilling(fieldValue);
    }
    props.updateNewCardDetails(fieldName, fieldValue);
  };
  
  if (!props.isSelected) return null;
  
  const savedAddressBillingLayout = window?.start?.settings?.use_saved_address_billing_layout;
  
  return (
    <FieldContainer2 key={1} fieldUpdateDispatcher={updateNewCardDetailsHandler}>
      {(fieldContainer) => {
        fieldContainerRef.current = fieldContainer;
        return (
          <div
            className={`fields new-card-details-fields ${classes.newCardForm}`}
            data-test="NewCardForm"
            id="new-card-details-fields"
          >
            <div className="fields credit-card-fields">
              <NewCardFormFieldSet
                definedFields={CreditCardFields(props.shouldUseExpiryInputField, props.isExpiryDateWave2Enabled, props.newBillingAddressLayout)}
                newCardDetails={props.newCardDetails}
                updateNewCardDetails={updateNewCardDetails}
                fieldContainer={fieldContainer}
                cardType={props.cardType}
                settings={props.settings}
                shouldUseExpiryInputField={props.shouldUseExpiryInputField}
              />
            </div>
            {props.shouldCaptureBillingAddress ? (
              (!window.start.settings.should_use_new_billing_address_layout && (
              <div className={classes.billingAddressTitle} data-test="NewCardForm__billingAddressTitle">
                <Translation
                  optionalExtension={props.siteCode}
                >
                  checkout.payment.method.field.label.billing.address.title
                </Translation>
              </div>
              )
              )
            ) : (
              <div className={`fields ${classes.billingAddressCheckboxContainer}`}>
                <NewCardFormFieldSet
                  definedFields={[{
                    ...UseShippingAddressField[0],
                    label: getBillingAddressTickboxText(props.paymentMethod, props.siteCode),
                  }]}
                  newCardDetails={props.newCardDetails}
                  fieldContainer={fieldContainer}
                  settings={props.settings}
                  shouldUseExpiryInputField={props.shouldUseExpiryInputField}
                />
                {props.showDeliveryAddressAsText && (
                  <div className="field" data-test="NewCardForm__orderDeliveryAddress">
                    <div className={classes.addressAsText}>
                      <AddressAsText {...props.orderDeliveryAddress} />
                    </div>
                  </div>
                )}
              </div>
            )}
            {props.showBillingAddressForm && (!savedAddressBillingLayout || !props.savedAddressExists) && (
              <div className={classes.billingAddress}>
                <BillingAddress
                  showForm={props.showBillingAddressForm}
                />
              </div>
            )}
            {props.showBillingAddressForm && savedAddressBillingLayout && props.savedAddressExists && (
              <ExpandingBillingAddressLayout
                showBillingAddressForm={props.showBillingAddressForm}
                savedAddressExists={props.savedAddressExists}
              />
            )}
            {props.hasOptionalConsent && (
              <StorePaymentOptionConsent
                fieldContainer={fieldContainer}
                paymentMethodType={props.paymentMethod.toUpperCase()}
              />
            )}
          </div>
        );
      }}
    </FieldContainer2>
  );
}

NewCardForm.propTypes = {
  paymentOptionIndex: PropTypes.number.isRequired,
  isSelected: PropTypes.bool.isRequired,
  newCardDetails: PropTypes.shape({
    cv2: PropTypes.string.isRequired,
  }).isRequired,
  orderDeliveryAddress: PropTypes.object.isRequired,
  paymentMethod: PropTypes.string.isRequired,
  cardType: PropTypes.shape({
    cv2Length: PropTypes.string.isRequired,
  }).isRequired,
  shouldCaptureBillingAddress: PropTypes.bool.isRequired,
  newBillingAddressLayout: PropTypes.bool,
  showDeliveryAddressAsText: PropTypes.bool.isRequired,
  showBillingAddressForm: PropTypes.bool.isRequired,
  hasStoredCard: PropTypes.bool.isRequired,
  hasOptionalConsent: PropTypes.bool.isRequired,
  settings: PropTypes.object.isRequired,
  siteCode: PropTypes.object,
  savedAddressExists: PropTypes.bool,
  shouldUseExpiryInputField: PropTypes.bool,
  isCardExpiryDateWave2Experiment: PropTypes.bool,
  // Actions
  updateNewCardDetails: PropTypes.func.isRequired,
  useDeliveryAddressForBilling: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const orderDeliveryAddress = state.start.deliveryAddress;
  const paymentMethodType = getSelectedPaymentOptionType(state);
  const savedAddresses = state.address.deliveryAddressList.addresses;
  const savedAddressesArray = state.address.deliveryAddressList.addresses ? Object.values(savedAddresses) : [];
  const savedAddressExists = savedAddressesArray.length > 1;
  const settings = getSettings(state);
  
  return {
    orderDeliveryAddress,
    newCardDetails: state.payment.newCardDetails || {},
    cardType: getCardType(state),
    shouldCaptureBillingAddress: shouldCaptureBillingAddress(state),
    showDeliveryAddressAsText: (!isUsingDifferentBillingAddress(state) && !!orderDeliveryAddress),
    newBillingAddressLayout: settings?.should_use_new_billing_address_layout,
    showBillingAddressForm: isUsingDifferentBillingAddress(state) && ownProps.isSelected,
    hasStoredCard: getHasStoredCard(state),
    hasOptionalConsent: getHasOptionalPaymentDetailsConsentCheckbox(paymentMethodType)(state),
    settings,
    paymentMethod: paymentMethodType.toLowerCase(),
    siteCode: getSiteCode(state),
    savedAddressExists,
    isCardExpiryDateWave2Experiment: getIsExpiryDateWave2Enabled(state),
    shouldUseExpiryInputField: getIsExpiryDateWave2Enabled(state) || settings?.should_use_expiry_date_input_field,
  };
};

const mapDispatchToProps = dispatch => ({
  updateNewCardDetails: (fieldName, fieldValue) => {
    dispatch(updateNewCardDetails(fieldName, fieldValue));
  },
  useDeliveryAddressForBilling: (fieldValue) => {
    dispatch(useDeliveryAddressForBilling(fieldValue));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(NewCardForm);
